<template>
  <div>
    <v-row>
      <v-col cols="12" sm="12" md="2"> </v-col>
      <v-col cols="12" sm="12" md="8">
        <v-card class="mx-auto my-5 mb-16" max-width="1000">
          <v-app-bar flat>
            <span class="headline">{{ title }}</span>
          </v-app-bar>
          <v-card-text>
            <v-container>
              <v-form ref="form" lazy-validation>
                <v-row>
                  <v-col cols="12" md="7">
                    <v-text-field
                      v-model="form.name"
                      :label="$t('environmentalReports.form.name')"
                      :rules="[
                        (v) => !!v || $t('general.fieldNeedsToBeFilled'),
                      ]"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-datetime-picker
                      v-model="form.date"
                      :label="$t('environmentalReports.form.date')"
                      prependIcon=""
                      :text-field-props="dateFieldProps"
                      :date-picker-props="datePickerProps"
                      :time-picker-props="timePickerProps"
                      dateFormat="dd.MM.yyyy"
                      :clear-text="$t('incidentReports.clear')"
                    />
                  </v-col>
                </v-row>

                <v-textarea
                  v-model="form.description"
                  :label="$t('environmentalReports.form.description')"
                  outlined
                  dense
                  rows="4"
                  auto-grow
                />

                <v-combobox
                  v-model="responsibleValue"
                  :items="users"
                  :label="$t('environmentalReports.form.responsible')"
                  item-text="name"
                  return-object
                  clearable
                  outlined
                  dense
                  class="mb-4"
                />

                <v-btn
                  v-if="canCreateTickets || !!form.ticket"
                  color="primary"
                  outlined
                  small
                  :loading="creatingTicket"
                  :disabled="!form.name || !!form.ticket"
                  @click="createTicket"
                >
                  <span v-if="!form.ticket">
                    {{ $t("inspections.createTicket") }}
                    <v-icon class="ml-1" small>launch</v-icon>
                  </span>
                  <span v-else>
                    {{ $t("inspections.ticketCreated") }}
                    <v-icon class="ml-1" small>check</v-icon>
                  </span>
                </v-btn>
              </v-form>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="grey" outlined @click="closeForm">
              {{ $t("general.back") }}
            </v-btn>
            <v-btn color="primary" :loading="saving" @click="saveForm">
              <div v-if="mode === 'new'">{{ $t("general.save") }}</div>
              <div v-else>{{ $t("general.saveChanges") }}</div>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col cols="12" sm="12" md="2"> </v-col>
    </v-row>
  </div>
</template>

<script>
import vDatetimePicker from "../../components/_utils/formComponent/generalFormComponents/v2DateTimePicker.vue";

function defaultForm() {
  return {
    name: "",
    date: new Date(),
    description: "",
    responsible: null,
    responsibleText: "",
    ticket: null,
  };
}

export default {
  name: "environmentalReportForm",
  components: { vDatetimePicker },
  data() {
    return {
      form: defaultForm(),
      users: [],
      responsibleValue: null,
      datePickerProps: { locale: "de-de", "first-day-of-week": "1" },
      timePickerProps: { format: "24hr" },
      saving: false,
      creatingTicket: false,
    };
  },
  computed: {
    mode() {
      return this.$route.params.mode;
    },
    title() {
      return this.mode === "new"
        ? this.$t("environmentalReports.createTitle")
        : this.$t("environmentalReports.editTitle");
    },
    dateFieldProps() {
      return {
        outlined: true,
        dense: true,
        "prepend-inner-icon": "mdi-calendar",
        rules: [(v) => !!v || this.$t("general.fieldNeedsToBeFilled")],
      };
    },
    targetPeriod() {
      if (!this.form.date) return null;
      const d = new Date(this.form.date);
      if (Number.isNaN(d.getTime())) return null;
      return { year: String(d.getFullYear()), month: String(d.getMonth() + 1) };
    },
  },
  async created() {
    this.users = (await this.$store.dispatch("getUsersData")) || [];

    if (this.mode !== "new") {
      const item = await this.$store.dispatch(
        "getSingleEnvironmentalReport",
        this.$route.params.id
      );
      if (item) {
        this.form = {
          ...defaultForm(),
          ...item,
          date: item.date ? new Date(item.date) : null,
          responsible:
            item.responsible && typeof item.responsible === "object"
              ? item.responsible._id
              : item.responsible || null,
          ticket:
            item.ticket && typeof item.ticket === "object"
              ? item.ticket._id
              : item.ticket || null,
        };

        if (this.form.responsible) {
          this.responsibleValue =
            this.users.find(
              (u) => String(u._id) === String(this.form.responsible)
            ) || null;
        } else if (this.form.responsibleText) {
          this.responsibleValue = this.form.responsibleText;
        }
      }
    }
  },
  methods: {
    closeForm() {
      this.$router.back();
    },
    async createTicket() {
      this.creatingTicket = true;
      try {
        const board = await this.$store.dispatch(
          "getSettingData",
          "standardOpenBoard"
        );
        const picked = this.responsibleValue;
        const responsibleUserId =
          picked && typeof picked === "object" && picked._id
            ? picked._id
            : null;

        const ticket = await this.$store.dispatch("addTicket", {
          recurring: 0,
          title: this.form.name,
          description: this.form.description,
          dueDate: null,
          users: responsibleUserId ? [responsibleUserId] : [],
          priority: 1,
          board: board.value,
        });
        if (ticket) this.form.ticket = ticket._id;
      } finally {
        this.creatingTicket = false;
      }
    },
    async saveForm() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        const picked = this.responsibleValue;
        const isUser = picked && typeof picked === "object" && picked._id;

        const payload = {
          name: this.form.name,
          date: this.form.date,
          description: this.form.description,
          responsible: isUser ? picked._id : null,
          responsibleText: isUser ? "" : String(picked || "").trim(),
          ticket: this.form.ticket || null,
        };

        if (this.mode === "new") {
          await this.$store.dispatch("createEnvironmentalReport", payload);
        } else {
          await this.$store.dispatch("updateEnvironmentalReport", {
            ...payload,
            id: this.$route.params.id,
          });
        }

        if (this.targetPeriod) {
          this.$router.push({
            name: "environmentalReportByMonth",
            params: {
              year: this.targetPeriod.year,
              month: this.targetPeriod.month,
            },
          });
          return;
        }

        this.closeForm();
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
